'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeviceType } from '@osmosis/nextjs-components/context/DeviceTypeProvider';
import { usePagesContext } from '@osmosis/nextjs-components/context';
import useOsmosisAI from '../hooks/useOsmosisAI';
import { useOAIIframeChannel } from '../hooks/useOAIIframeChannel';
import { track } from '../lib/util/tracking';
import { getErrorMetadata } from '../lib/util/osmosisAiRetry';
import {
  OAIInboundMessage,
  OAIMode,
  resolveOAILearnPath,
} from '../lib/util/oai';
import {
  AIPanelInitialState,
  AIPanelPage,
  AIPanelOptions,
  getPanelOptions,
  DEFAULT_PAGE_KEY,
} from './AIPanelContext.utils';

// Re-export so existing client-component imports continue to work.
export { AIPanelInitialState, AIPanelPage, getPanelOptions, DEFAULT_PAGE_KEY };
export type { AIPanelOptions };

type UpdateOAIPageContextProps = {
  useInitialPageContext?: boolean;
  pageContext?: string;
};

export type OpenInNewTabOptions = {
  withQuery?: boolean;
  shouldInitFreshChat?: boolean;
};

export type OpenPanelOptions = {
  force?: boolean;
  shouldInitFreshChat?: boolean;
};

const STORAGE_KEY = 'ai-panel';
const INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000;
const DEFAULT_WIDTH_RATIO = 0.32;
export const PANEL_MIN_WIDTH = 360;
const MAX_WIDTH_RATIO = 0.5;
const LAYOUT_OVERHEAD = 50;
// On a fresh iframe load, OAI's own chat-history sidebar is still loading
// when it announces READY. Submitting immediately races that load and the
// sidebar ends up empty until a hard reload, so we give it a moment first.
// Messages sent to an already-ready (warm) iframe aren't delayed.
const PENDING_PROMPT_FLUSH_DELAY_MS = 1000;

type PagePanelState = {
  open?: boolean;
  width?: number;
  lastInteraction?: Record<string, number>;
};

type AIPanelStore = Record<string, PagePanelState>;

export type AIPanelContextProps = {
  isOpen: boolean;
  isAIAvailable: boolean;
  isLoggedOut: boolean;
  panelWidth: number;
  isDragging: boolean;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  iframeSrc: string | null;
  iframeLoading: boolean;
  iframeError: boolean;
  isAILockoutOpen: boolean;
  isOAIIframeReady: boolean;
  /** Topic/title for the current page (e.g. Video title on the Learn page). */
  topic: string | undefined;
  showExamplePrompts: boolean;
  promptsExamplesCollapsed: boolean;
  /** True once the user has submitted a prompt; hides the example prompts. */
  promptSubmitted: boolean;
  showAIButton: boolean;
  shouldRenderAIChat: boolean;
  openPanel: (searchQuery?: string, options?: OpenPanelOptions) => void;
  closePanel: (options?: { restoreFocus?: boolean }) => void;
  setPanelWidth: (width: number) => void;
  setIsDragging: (dragging: boolean) => void;
  setIsAILockoutOpen: (open: boolean) => void;
  retryLoadIframe: () => void;
  recordInteraction: () => void;
  openInNewTab: (options?: OpenInNewTabOptions) => void;
  updateOAIPageContext: (context: UpdateOAIPageContextProps) => void;
  sendOAIUserMessage: (message: string, mode?: OAIMode) => void;
  /** Opens the panel and sends a prompt, deferring until the iframe is ready. */
  sendPromptOpeningPanel: (
    prompt: string,
    mode?: OAIMode,
    pageContext?: string,
  ) => void;
  togglePromptsExamples: () => void;
};

const noOp = () => undefined;

export const AIPanelContext = createContext<AIPanelContextProps>({
  isOpen: false,
  isAIAvailable: false,
  isLoggedOut: false,
  panelWidth: 0,
  isDragging: false,
  iframeRef: { current: null },
  iframeSrc: null,
  iframeLoading: false,
  iframeError: false,
  isAILockoutOpen: false,
  isOAIIframeReady: false,
  topic: undefined,
  showExamplePrompts: false,
  promptsExamplesCollapsed: false,
  promptSubmitted: false,
  showAIButton: false,
  shouldRenderAIChat: false,
  openPanel: noOp,
  closePanel: noOp,
  setPanelWidth: noOp,
  setIsDragging: noOp,
  setIsAILockoutOpen: noOp,
  retryLoadIframe: noOp,
  recordInteraction: noOp,
  openInNewTab: noOp,
  updateOAIPageContext: noOp,
  sendOAIUserMessage: noOp,
  sendPromptOpeningPanel: noOp,
  togglePromptsExamples: noOp,
});

/**
 * Hook to access the AI panel context.
 */
export const useAIPanelContext = (): AIPanelContextProps =>
  useContext(AIPanelContext);

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function readStore(): AIPanelStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed as AIPanelStore;
    }
    return {};
  } catch {
    return {};
  }
}

function writeStore(store: AIPanelStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function readPageState(identifier: string): PagePanelState {
  return readStore()[identifier] ?? {};
}

function writePageState(
  identifier: string,
  patch: Partial<PagePanelState>,
): void {
  const store = readStore();
  store[identifier] = { ...store[identifier], ...patch };
  writeStore(store);
}

function cleanExpiredInteractions(
  map: Record<string, number>,
): Record<string, number> {
  const now = Date.now();
  const cleaned: Record<string, number> = {};
  for (const [key, ts] of Object.entries(map)) {
    if (now - ts <= INACTIVITY_THRESHOLD_MS) {
      cleaned[key] = ts;
    }
  }
  return cleaned;
}

function writeInteraction(identifier: string, interactionKey: string): void {
  const store = readStore();
  const page = store[identifier] ?? {};
  const cleaned = cleanExpiredInteractions(page.lastInteraction ?? {});
  cleaned[interactionKey] = Date.now();
  store[identifier] = { ...page, lastInteraction: cleaned };
  writeStore(store);
}

function readInteraction(
  identifier: string,
  interactionKey: string,
): number | undefined {
  const page = readPageState(identifier);
  const cleaned = cleanExpiredInteractions(page.lastInteraction ?? {});
  return cleaned[interactionKey];
}

/**
 * Provides AI panel state (open/closed, width) with localStorage persistence.
 */
export const AIPanelProvider = ({
  children,
  searchQuery: searchQueryProp,
  panelOptions,
  initialPageContext,
  shouldInitFreshChat: defaultShouldInitFreshChat = true,
  topic,
  showExamplePrompts = false,
  showLoggedOut = true,
  keepSidebarOpen,
  disabled = false,
}: {
  children: React.ReactNode;
  searchQuery?: string;
  panelOptions: AIPanelOptions;
  initialPageContext?: string;
  shouldInitFreshChat?: boolean;
  topic?: string;
  showExamplePrompts?: boolean;
  showLoggedOut?: boolean;
  keepSidebarOpen?: boolean;
  /** Skips auto-open and iframe loading entirely — for pages that don't render the panel. */
  disabled?: boolean;
}) => {
  const { screenWidth, isMobile } = useDeviceType();
  const [isOpen, setIsOpen] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [isAILockoutOpen, setIsAILockoutOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [promptsExamplesCollapsed, setPromptsExamplesCollapsed] =
    useState(false);
  // Set once the user submits any prompt to the chat. Hides the example prompts
  // for the rest of the session (the TrustStrip stays). Kept separate from
  // `promptsExamplesCollapsed` so the collapse/expand toggle is unaffected.
  const [promptSubmitted, setPromptSubmitted] = useState(false);

  const togglePromptsExamples = useCallback(() => {
    setPromptsExamplesCollapsed((prev) => !prev);
  }, []);

  const {
    isAIAvailable,
    shouldShowLockout,
    isLoggedOut,
    openInNewTab: openInNewTabFromHook,
    getIframeSrc,
    showAIButton,
    isUserLoaded,
  } = useOsmosisAI(showLoggedOut);
  const { pathname } = usePagesContext();

  const onIframeReady = useCallback(() => {
    setIframeLoading(false);
    track('osmosis-ai-embed-loaded', { page: pathname }, ['keen', 'pendo']);
  }, [pathname]);

  const onPromptSubmitted = useCallback(() => {
    setPromptSubmitted(true);
    track('osmosis-ai-prompt-submitted', { page: pathname }, ['keen', 'pendo']);
  }, [pathname]);

  const onLearnPageClick = useCallback(
    (message: OAIInboundMessage) => {
      const path = resolveOAILearnPath(message);
      if (!path) return;
      track('osmosis-ai-learn-page-click', { page: pathname }, [
        'keen',
        'pendo',
      ]);
      window.open(
        `${window.location.origin}${path}`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [pathname],
  );

  const {
    isReady: isOAIIframeReady,
    updatePageContext,
    sendUserMessage: sendOAIUserMessage,
  } = useOAIIframeChannel({
    iframeRef,
    iframeSrc,
    onIframeReady,
    onPromptSubmitted,
    onLearnPageClick,
  });

  const lastSearchQueryRef = useRef<string | undefined>(undefined);
  const isFetchingRef = useRef(false);
  const loadIframeRef = useRef<
    (
      searchQuery?: string,
      force?: boolean,
      shouldInitFreshChat?: boolean,
    ) => Promise<void>
  >(async () => {
    // no-op until initialized
  });

  const loadIframe = useCallback(
    async (
      searchQuery?: string,
      force?: boolean,
      shouldInitFreshChat = defaultShouldInitFreshChat,
    ) => {
      if (!force && iframeSrc) return;
      if (isFetchingRef.current) return;
      if (force) setIframeSrc(null);
      isFetchingRef.current = true;
      setIframeLoading(true);
      setIframeError(false);
      try {
        const interactionKey = searchQuery || DEFAULT_PAGE_KEY;
        const lastInteraction = readInteraction(
          panelOptions.identifier,
          interactionKey,
        );
        let url: string;
        if (!lastInteraction) {
          url = await getIframeSrc({
            searchQuery,
            pageContext: initialPageContext,
            ...(shouldInitFreshChat === false
              ? { shouldInitFreshChat: false }
              : {}),
          });
        } else {
          url = await getIframeSrc({
            pageContext: initialPageContext,
            ...(shouldInitFreshChat === false
              ? { shouldInitFreshChat: false }
              : {}),
          });
        }
        setIframeSrc(url);
        // Keep `iframeLoading` true here — it flips to false in `onIframeReady`
        // once the iframe announces SHERPATH_AI_IFRAME_READY.
        writeInteraction(panelOptions.identifier, interactionKey);
      } catch (err) {
        setIframeError(true);
        setIframeLoading(false);
        track(
          'osmosis-ai-open-error',
          { page: pathname, ...getErrorMetadata(err) },
          ['keen', 'pendo'],
        );
      } finally {
        isFetchingRef.current = false;
      }
    },
    [
      defaultShouldInitFreshChat,
      iframeSrc,
      getIframeSrc,
      pathname,
      panelOptions.identifier,
    ],
  );

  const openInNewTab = useCallback(
    ({
      withQuery = false,
      shouldInitFreshChat = defaultShouldInitFreshChat,
    }: OpenInNewTabOptions = {}) => {
      openInNewTabFromHook({
        searchQuery: withQuery ? searchQueryProp : undefined,
        pageContext: initialPageContext,
        ...(shouldInitFreshChat === false
          ? { shouldInitFreshChat: false }
          : {}),
      });
    },
    [openInNewTabFromHook, searchQueryProp, initialPageContext],
  );

  loadIframeRef.current = loadIframe;

  const isEnabled =
    !disabled &&
    ((showAIButton && !shouldShowLockout) ||
      (showAIButton && keepSidebarOpen && isUserLoaded));

  const shouldRenderAIChat = !disabled && showAIButton && !shouldShowLockout;

  // Read from localStorage on mount (client only)
  useEffect(() => {
    if (!isEnabled) return; // Only initialize if the feature is enabled, otherwise the panel will be closed and hidden.
    const { identifier } = panelOptions;
    const pageState = readPageState(identifier);

    const defaultWidth = Math.round(
      (window.innerWidth - LAYOUT_OVERHEAD) * DEFAULT_WIDTH_RATIO,
    );
    const maxWidth = Math.round(
      (window.innerWidth - LAYOUT_OVERHEAD) * MAX_WIDTH_RATIO,
    );
    const clamped = clamp(
      pageState?.width ?? defaultWidth,
      PANEL_MIN_WIDTH,
      maxWidth,
    );
    setPanelWidthState(clamped);

    const defaultOpen = isMobile
      ? panelOptions.mobile.initialState === AIPanelInitialState.Expanded
      : panelOptions.desktop.initialState === AIPanelInitialState.Expanded;
    // Decide the initial open state:
    //  - The user's saved preference always wins (open if they opened it,
    //    closed if they closed it) so the panel never collapses on navigation.
    //  - With no saved preference, standard pages fall back to `defaultOpen`
    //    (open by default on desktop).
    //  - Video pages are the exception: they must not auto-open, because these
    //    SEO pages shouldn't load the OAI iframe by default. So with no saved
    //    preference they stay closed; an explicit "open" is still honored.
    const isVideoPanel = identifier === AIPanelPage.Video;
    const shouldOpen = isVideoPanel
      ? pageState.open === true // video: closed by default, open only if the user chose to
      : (pageState.open ?? defaultOpen); // standard: user preference, else open by default
    setIsOpen(shouldOpen);
    if (shouldOpen && !isLoggedOut) {
      loadIframeRef.current(searchQueryProp);
    }
  }, [isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-clamp panel width whenever the viewport is resized
  useEffect(() => {
    if (!isEnabled) return;
    if (screenWidth === 0) return;
    const { identifier } = panelOptions;
    const pageState = readPageState(identifier);
    const maxWidth = Math.round(
      (screenWidth - LAYOUT_OVERHEAD) * MAX_WIDTH_RATIO,
    );
    setPanelWidthState((prev) => {
      const base = pageState?.width ?? prev;
      return clamp(base, PANEL_MIN_WIDTH, maxWidth);
    });
  }, [screenWidth, isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the searchQuery prop changes (page navigation), force-reload the iframe
  const prevSearchQueryPropRef = useRef(searchQueryProp);
  useEffect(() => {
    if (
      !isLoggedOut &&
      searchQueryProp &&
      searchQueryProp !== prevSearchQueryPropRef.current &&
      isOpen
    ) {
      lastSearchQueryRef.current = searchQueryProp;
      isFetchingRef.current = false;
      loadIframeRef.current(searchQueryProp, true);
    }
    prevSearchQueryPropRef.current = searchQueryProp;
  }, [searchQueryProp, isOpen, isLoggedOut]);

  // Close panel if the feature flag is disabled or user loses eligibility
  useEffect(() => {
    if (!isEnabled) {
      setIsOpen(false);
    }
  }, [isEnabled]);

  const openPanel = useCallback(
    (
      searchQuery?: string,
      {
        force = false,
        shouldInitFreshChat = defaultShouldInitFreshChat,
      }: OpenPanelOptions = {},
    ) => {
      setIsOpen(true);
      writePageState(panelOptions.identifier, { open: true });
      // Fired unconditionally to stay symmetric with the `-collapsed` event in
      // closePanel (analytics-only; no visual change to the shared panel).
      track('osmosis-ai-embed-expanded', { page: pathname }, ['keen', 'pendo']);
      lastSearchQueryRef.current = searchQuery;
      if (isLoggedOut) return;
      if (force) {
        isFetchingRef.current = false;
        loadIframe(searchQuery, true, shouldInitFreshChat);
      } else {
        loadIframe(searchQuery, false, shouldInitFreshChat);
      }
    },
    [
      defaultShouldInitFreshChat,
      loadIframe,
      panelOptions.identifier,
      isLoggedOut,
      pathname,
    ],
  );

  const closePanel = useCallback(
    (options?: { restoreFocus?: boolean }) => {
      setIsOpen(false);
      writePageState(panelOptions.identifier, { open: false });
      track('osmosis-ai-embed-collapsed', { page: pathname }, [
        'keen',
        'pendo',
      ]);
      // Callers that manage their own focus (e.g. the mobile section overlay,
      // which returns focus to its bottom-nav button) opt out of the default
      // restoration to the global Osmosis AI button.
      if (options?.restoreFocus === false) return;
      setTimeout(() => {
        const osmosisAIButton = document.querySelector<HTMLElement>(
          '[data-testid="osmosis-ai-btn"]',
        );
        osmosisAIButton?.focus({ preventScroll: true });
      }, 0);
    },
    [pathname, panelOptions.identifier],
  );

  const retryLoadIframe = useCallback(() => {
    setIframeSrc(null);
    setIframeError(false);
    isFetchingRef.current = false;
    loadIframe(lastSearchQueryRef.current, true);
  }, [loadIframe]);

  const recordInteraction = useCallback(() => {
    const interactionKey = lastSearchQueryRef.current || DEFAULT_PAGE_KEY;
    writeInteraction(panelOptions.identifier, interactionKey);
  }, [panelOptions.identifier]);

  const setPanelWidth = useCallback(
    (width: number) => {
      const maxWidth = Math.round(
        (window.innerWidth - LAYOUT_OVERHEAD) * MAX_WIDTH_RATIO,
      );
      const clamped = clamp(width, PANEL_MIN_WIDTH, maxWidth);
      setPanelWidthState(clamped);
      writePageState(panelOptions.identifier, { width: clamped });
    },
    [panelOptions.identifier],
  );

  const updateOAIPageContext = useCallback(
    ({
      useInitialPageContext = false,
      pageContext,
    }: UpdateOAIPageContextProps) => {
      const context = useInitialPageContext ? initialPageContext : pageContext;
      if (!context) return;
      updatePageContext(context);
    },
    [initialPageContext, updatePageContext],
  );

  // Opens the panel and submits a prompt. When the panel is closed the OAI
  // iframe may not be loaded yet, so if it isn't ready we hold the prompt and
  // flush it once the iframe announces READY (effect below).
  const pendingPromptRef = useRef<{
    prompt: string;
    mode: OAIMode;
    pageContext?: string;
  } | null>(null);

  const sendPromptOpeningPanel = useCallback(
    (prompt: string, mode?: OAIMode, pageContext?: string) => {
      const resolvedMode = mode ?? OAIMode.DefaultStudy;
      openPanel();
      if (isOAIIframeReady) {
        updateOAIPageContext(
          pageContext ? { pageContext } : { useInitialPageContext: true },
        );
        sendOAIUserMessage(prompt, resolvedMode);
      } else {
        pendingPromptRef.current = { prompt, mode: resolvedMode, pageContext };
      }
    },
    [openPanel, isOAIIframeReady, updateOAIPageContext, sendOAIUserMessage],
  );

  useEffect(() => {
    if (!isOAIIframeReady || !pendingPromptRef.current) return;
    const timeoutId = setTimeout(() => {
      const pending = pendingPromptRef.current;
      if (!pending) return;
      pendingPromptRef.current = null;
      updateOAIPageContext(
        pending.pageContext
          ? { pageContext: pending.pageContext }
          : { useInitialPageContext: true },
      );
      sendOAIUserMessage(pending.prompt, pending.mode);
    }, PENDING_PROMPT_FLUSH_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [isOAIIframeReady, updateOAIPageContext, sendOAIUserMessage]);

  // Drop any queued prompt if the iframe fails to load so it can't fire late.
  useEffect(() => {
    if (iframeError) pendingPromptRef.current = null;
  }, [iframeError]);

  // Non-prod dev helpers: expose `window.__oai` for manual postMessage testing
  // from the browser console. Gated to non-production hosts; cleaned up on
  // teardown or when the iframe leaves the READY state.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.location.hostname === 'www.osmosis.org') return undefined;
    if (!isOAIIframeReady) return undefined;
    (window as unknown as Record<string, unknown>).__oai = {
      updatePageContext,
      sendUserMessage: sendOAIUserMessage,
    };
    return () => {
      delete (window as unknown as Record<string, unknown>).__oai;
    };
  }, [isOAIIframeReady, updatePageContext, sendOAIUserMessage]);

  const contextValue = useMemo<AIPanelContextProps>(
    () => ({
      isOpen,
      isAIAvailable,
      isLoggedOut,
      panelWidth,
      isDragging,
      iframeRef,
      iframeSrc,
      iframeLoading,
      iframeError,
      isAILockoutOpen,
      isOAIIframeReady,
      topic,
      showExamplePrompts,
      promptsExamplesCollapsed,
      promptSubmitted,
      showAIButton,
      shouldRenderAIChat,
      openPanel,
      closePanel,
      setPanelWidth,
      setIsDragging,
      setIsAILockoutOpen,
      retryLoadIframe,
      recordInteraction,
      openInNewTab,
      updateOAIPageContext,
      sendOAIUserMessage,
      sendPromptOpeningPanel,
      togglePromptsExamples,
    }),
    [
      isOpen,
      isAIAvailable,
      isLoggedOut,
      panelWidth,
      isDragging,
      iframeSrc,
      iframeLoading,
      iframeError,
      isAILockoutOpen,
      isOAIIframeReady,
      topic,
      showExamplePrompts,
      promptsExamplesCollapsed,
      promptSubmitted,
      showAIButton,
      shouldRenderAIChat,
      openPanel,
      closePanel,
      setPanelWidth,
      retryLoadIframe,
      recordInteraction,
      openInNewTab,
      updateOAIPageContext,
      sendOAIUserMessage,
      sendPromptOpeningPanel,
      togglePromptsExamples,
    ],
  );

  return (
    <AIPanelContext.Provider value={contextValue}>
      {children}
    </AIPanelContext.Provider>
  );
};
